/**
 * Stale-chunk handler — catches the "Failed to fetch dynamically imported
 * module" class of errors that fire when a new build has been deployed
 * while the user still has the old index.html in memory. These errors
 * come from two places:
 *
 *   1. A React-render lazy import (e.g. <Suspense> route load) — caught
 *      by ErrorBoundary, which shows a polished "new version available"
 *      panel. See error-boundary.tsx.
 *
 *   2. An async user action (event handler that triggers `import()`) —
 *      bubbles up as an unhandledrejection / window error, NOT through
 *      React. This module wires the global listener for that path: it
 *      reloads the page exactly once per session so the user doesn't
 *      sit on a broken state, with a sessionStorage guard so a reload
 *      loop is impossible if the first reload doesn't fix it.
 */

const STORAGE_KEY = "campushub:stale-chunk-reload";

function isStaleChunkMessage(message: unknown): boolean {
  if (typeof message !== "string") return false;
  const m = message.toLowerCase();
  return (
    m.includes("failed to fetch dynamically imported module") ||
    m.includes("error loading dynamically imported module") ||
    m.includes("importing a module script failed") ||
    m.includes("chunkloaderror") ||
    (m.includes("loading chunk") && m.includes("failed"))
  );
}

/** True for a failed dynamic `import()` of a chunk (stale deploy, offline tab) — not for a bug inside the module. */
export function isStaleChunkError(err: unknown): boolean {
  if (!err) return false;
  if (err instanceof Error) {
    return isStaleChunkMessage(err.message) || isStaleChunkMessage(err.name);
  }
  if (typeof err === "object" && err !== null && "message" in err) {
    return isStaleChunkMessage((err as { message?: unknown }).message);
  }
  return isStaleChunkMessage(err);
}

function maybeReloadOnce() {
  let already = false;
  try {
    already = sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // sessionStorage can throw in private-browsing / locked-down contexts.
    // Fall back to in-memory flag via a window prop so we still avoid loops.
    already = (window as Window & { __chubStaleReload?: boolean }).__chubStaleReload === true;
  }
  if (already) {
    console.warn("[stale-chunk] reload already attempted this session — not retrying.");
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, "1");
  } catch {
    (window as Window & { __chubStaleReload?: boolean }).__chubStaleReload = true;
  }
  console.warn("[stale-chunk] detected — reloading to pick up new build.");
  window.location.reload();
}

export function installStaleChunkHandler() {
  window.addEventListener("error", (event) => {
    if (isStaleChunkError(event.error) || isStaleChunkMessage(event.message)) {
      event.preventDefault();
      maybeReloadOnce();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleChunkError(event.reason)) {
      event.preventDefault();
      maybeReloadOnce();
    }
  });
}
